import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useUpdateAdminPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const updatePassword = useCallback(
    async ({ email, newPassword }) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");
      setMessage("");
      // don’t reset success immediately – keep last state until response

      try {
        // Ensure WebSocket connection
        if (!isConnected) {
          if (!connectionAttemptedRef.current) {
            connectionAttemptedRef.current = true;
            await connect();
          }

          await new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(interval);
                reject(new Error("Component unmounted"));
              } else if (isOpen()) {
                clearInterval(interval);
                resolve(true);
              } else if (Date.now() - start > 10000) {
                clearInterval(interval);
                reject(new Error("Connection timeout"));
              }
            }, 100);
          });
        }

        // Send update password request
        const response = await send("cms_update_user_password", {
          email,
          new_password: newPassword,
        });

        console.log("Update password response:", response);

        if (!response || response.type !== "cms_update_user_password_response") {
          throw new Error("Invalid response from server");
        }

        if (!response.success) {
          throw new Error(response.message || "Failed to update password");
        }

        setSuccess(true);
        setMessage(response.message || "Password updated successfully");
        window.location.reload();
        return { success: true, message: response.message };
      } catch (err) {
        setError(err.message);
        setSuccess(false);
        return { success: false, error: err.message };
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, isOpen, connect, send]
  );

  // Optional server push handling
  useEffect(() => {
    const unregister = registerHandler("update_password_update", (msg) => {
      if (mountedRef.current) {
        console.log("Update password push:", msg);
      }
    });
    return () => unregister();
  }, [registerHandler]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { isLoading, error, success, message, updatePassword };
}
